import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentMethodForm from '../PaymentMethodForm';
import { useApi } from '../../services/api';

console.log('Stripe Key:', process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ? 'Present' : 'MISSING');
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  .then(stripe => {
    console.log('Stripe loaded successfully:', !!stripe);
    return stripe;
  })
  .catch(error => {
    console.error('Stripe loading error:', error);
    throw error;
  });

const WizardStepPaymentMethodSelection = ({
  onNext,
  onBack,
  paymentMethods: paymentMethodsProp,
  wizardData,
  setWizardData
}) => {
  const [paymentMethods, setPaymentMethods] = useState(paymentMethodsProp || []);
  const [loading, setLoading] = useState(false);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(
    wizardData.selected_payment_method_id || ((!paymentMethods || paymentMethods.length === 0) ? 'new_card' : null)
  );
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentFormLoading, setPaymentFormLoading] = useState(false);

  const api = useApi();

  // Fetch payment methods directly without Redux to avoid triggering Dashboard re-renders
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        console.log('Fetching payment methods directly...');
        const response = await api.getPaymentMethods();
        console.log('Payment methods API response:', response);
        console.log('Payment methods data:', response.data);

        // Backend returns payment_methods array
        const methods = response.data?.payment_methods || response.data || [];
        console.log('Setting payment methods:', methods);
        setPaymentMethods(methods);
      } catch (error) {
        console.error('Error fetching payment methods:', error);
        setPaymentMethods([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentMethods();
  }, []); // Empty deps - only fetch once on mount

  const handlePaymentMethodSelect = (paymentMethodId) => {
    setSelectedPaymentMethodId(paymentMethodId);
    setShowPaymentForm(paymentMethodId === 'new_card');
    setWizardData(prev => ({
      ...prev,
      selected_payment_method_id: paymentMethodId
    }));
  };

  const handleContinue = () => {
    if (!selectedPaymentMethodId) {
      alert('Payment method selection required');
      return;
    }

    // If existing payment method selected, go directly to final step
    if (selectedPaymentMethodId !== 'new_card') {
      onNext();
    }
    // If new card, the form will handle submission
  };

  const handlePaymentMethodSuccess = async (paymentMethodId) => {
    console.log('Payment method added successfully:', paymentMethodId);

    // Refetch payment methods to show the new one in the list
    try {
      const response = await api.getPaymentMethods();
      const methods = response.data?.payment_methods || response.data || [];
      console.log('Refetched payment methods:', methods);
      setPaymentMethods(methods);
    } catch (error) {
      console.error('Error refetching payment methods:', error);
    }

    // Update wizard data with the new payment method
    setWizardData(prev => ({
      ...prev,
      selected_payment_method_id: paymentMethodId,
      payment_method_id: paymentMethodId
    }));

    // Call onNext with the payment method ID to trigger subscription creation
    onNext(paymentMethodId);
  };

  const handlePaymentFormCancel = () => {
    // Reset to no selection
    setSelectedPaymentMethodId(null);
    setShowPaymentForm(false);
    setWizardData(prev => ({
      ...prev,
      selected_payment_method_id: null
    }));
  };

  const formatCardBrand = (brand) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳',
      diners: '💳',
      jcb: '💳',
      unionpay: '💳'
    };
    return icons[brand] || '💳';
  };

  // Auto-select default payment method when payment methods are loaded
  React.useEffect(() => {
    console.log('PaymentMethodSelection useEffect:', {
      paymentMethods: paymentMethods?.length,
      selectedPaymentMethodId,
      wizardSelected: wizardData.selected_payment_method_id
    });

    if (paymentMethods && paymentMethods.length > 0) {
      // Only auto-select if no payment method is currently selected
      if (!selectedPaymentMethodId && !wizardData.selected_payment_method_id) {
        // Find default payment method or use first one
        const defaultPaymentMethod = paymentMethods.find(pm => pm.is_default) || paymentMethods[0];
        console.log('Auto-selecting default payment method:', defaultPaymentMethod.id);
        setSelectedPaymentMethodId(defaultPaymentMethod.id);
        setShowPaymentForm(false);
        console.log('Calling setWizardData with default payment method');
        setWizardData(prev => ({
          ...prev,
          selected_payment_method_id: defaultPaymentMethod.id
        }));
        console.log('setWizardData called successfully');
      }
    } else if (!paymentMethods || paymentMethods.length === 0) {
      console.log('No payment methods, auto-selecting new_card');
      setSelectedPaymentMethodId('new_card');
      setShowPaymentForm(true);
      console.log('Calling setWizardData with new_card');
      setWizardData(prev => ({
        ...prev,
        selected_payment_method_id: 'new_card'
      }));
      console.log('setWizardData called successfully');
    }
  }, [paymentMethods]); // setWizardData is stable and doesn't need to be in dependencies

  return (
    <div className="min-h-[450px] bg-gradient-to-br from-gray-50 to-gray-200 py-3 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Content Section */}
        <div className="flex justify-center">
          <div className="w-full max-w-2xl">
            <div className="space-y-4">
              {paymentMethods && paymentMethods.length > 0 ? (
                <>
                  <h6 className="mb-3 text-lg font-semibold text-gray-800">Choose a payment method:</h6>
                  {/* Sort payment methods with default first, then by ID */}
                  {[...paymentMethods]
                    .sort((a, b) => {
                      // Default card first
                      if (a.is_default && !b.is_default) return -1;
                      if (!a.is_default && b.is_default) return 1;
                      // Then sort by ID for consistent ordering
                      return a.id.localeCompare(b.id);
                    })
                    .map((pm) => (
                      <div
                        key={pm.id}
                        className={`
                          bg-white rounded-xl p-4 cursor-pointer border-2 transition-all
                          ${selectedPaymentMethodId === pm.id
                            ? 'border-blue-500 shadow-lg ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-500'
                          }
                        `}
                        onClick={() => handlePaymentMethodSelect(pm.id)}
                      >
                        <div className="flex items-center">
                          <div className="mr-3">
                            <input
                              type="radio"
                              name="paymentMethod"
                              checked={selectedPaymentMethodId === pm.id}
                              onChange={() => handlePaymentMethodSelect(pm.id)}
                              className="w-4 h-4"
                            />
                          </div>
                          <div className="mr-3 text-2xl">
                            {getCardIcon(pm.brand)}
                          </div>
                          <div className="flex-grow">
                            <div className="flex items-center gap-2">
                              <strong className="text-gray-800">
                                {formatCardBrand(pm.brand)} •••• {pm.last4}
                              </strong>
                              {pm.is_default && (
                                <span className="px-2 py-1 bg-blue-500 text-white text-xs font-bold rounded">
                                  Default
                                </span>
                              )}
                            </div>
                            <small className="text-gray-600">
                              Expires {pm.exp_month.toString().padStart(2, '0')}/{pm.exp_year}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}

                  <div className="border-t border-gray-300 my-4"></div>
                </>
              ) : (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <h6 className="font-semibold text-blue-900 mb-2">No saved payment methods</h6>
                  <p className="text-blue-800 text-sm">You'll need to add a payment method to create your subscription.</p>
                </div>
              )}

              {/* Add new payment method option */}
              <div
                className={`
                  bg-white rounded-xl p-4 cursor-pointer transition-all
                  ${selectedPaymentMethodId === 'new_card'
                    ? 'border-2 border-blue-500 shadow-lg ring-2 ring-blue-200'
                    : 'border-2 border-dashed border-gray-300 hover:border-blue-500'
                  }
                `}
                onClick={() => handlePaymentMethodSelect('new_card')}
              >
                <div className="flex items-center">
                  <div className="mr-3">
                    <input
                      type="radio"
                      name="paymentMethod"
                      checked={selectedPaymentMethodId === 'new_card'}
                      onChange={() => handlePaymentMethodSelect('new_card')}
                      className="w-4 h-4"
                    />
                  </div>
                  <div className="mr-3 text-2xl">➕</div>
                  <div className="flex-grow">
                    <strong className="text-gray-800">Add new payment method</strong>
                    <br />
                    <small className="text-gray-600">
                      Add a new credit or debit card (we'll validate it with a $1 charge that will be refunded)
                    </small>
                  </div>
                </div>
              </div>

              {/* Inline Payment Form when new card is selected */}
              {showPaymentForm && (
                <div className="mt-4 animate-slideDown">
                  <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                    <h6 className="mb-4 text-lg font-semibold text-gray-800">Payment Information</h6>
                    {stripePromise ? (
                      <Elements stripe={stripePromise}>
                        <PaymentMethodForm
                          onSuccess={handlePaymentMethodSuccess}
                          onCancel={handlePaymentFormCancel}
                          buttonText="Save Payment Method & Continue"
                          showCancelButton={paymentMethods && paymentMethods.length > 0}
                          loading={paymentFormLoading}
                          setLoading={setPaymentFormLoading}
                        />
                      </Elements>
                    ) : (
                      <div className="text-red-600">
                        Error loading payment form. Please check console for details.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div className="flex justify-between mt-4 max-w-2xl mx-auto">
          <button
            onClick={onBack}
            className="bg-white text-black px-6 py-2 rounded-full font-bold border-2 border-black hover:bg-gray-50 transition-colors"
          >
            <i className="bi bi-arrow-left mr-2"></i>
            Back
          </button>
          {!showPaymentForm && (
            <button
              onClick={handleContinue}
              disabled={!selectedPaymentMethodId}
              className={`px-6 py-2 rounded-full font-bold transition-colors min-w-[140px] ${
                selectedPaymentMethodId
                  ? 'bg-black text-white hover:bg-gray-800'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              Create New Goal
              <i className="bi bi-arrow-right ml-2"></i>
            </button>
          )}
        </div>

        {!selectedPaymentMethodId && (
          <small className="text-red-600 mt-2 block text-center">Please select a payment method to continue</small>
        )}
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default WizardStepPaymentMethodSelection;
